import React, { useState } from "react";
import { Badge } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IProtocol } from "../../../shared/interfaces/protocol/IProtocol";
import DeleteModal from "../../../shared/components/DeleteModal";
import { ProtocolService } from "../service";
import { handleError } from "../../../shared/functions/handleError";
import { enqueueSnackbar } from "notistack";
import PageSizeSelect from "../../../shared/components/PageSizeSelect";
import Pagination from "../../../shared/components/Pagination";
import { IPagination } from "../../../shared/interfaces/shared/IPagination";

interface IProps {
    protocols: IProtocol[];
    pagination: IPagination;
    totalPages: number;
    handleUpdateList: () => void;
    changeFilterHandler: (
        name: string,
        value: string | number | boolean | number[] | null,
    ) => void;
}

const ProtocolList = ({
    protocols,
    pagination,
    totalPages,
    handleUpdateList,
    changeFilterHandler,
}: IProps) => {
    const { t } = useTranslation();

    const [isDeleteVisible, setIsDeleteVisible] = useState(false);
    const [selectedProtocol, setSelectedProtocol] = useState<IProtocol | undefined>(
        undefined,
    );

    const handleCloseDelete = () => {
        setIsDeleteVisible(false);
        setSelectedProtocol(undefined);
    };

    const handleDeleteProtocol = (id: number) => {
        ProtocolService.delete(id)
            .then((response) => {
                if (response) {
                    handleUpdateList();
                    handleCloseDelete();
                    enqueueSnackbar(
                        t("toasts.successfullyDeleted", {
                            name: selectedProtocol?.title,
                        }),
                        {
                            variant: "success",
                        },
                    );
                }
            })
            .catch(handleError);
    };

    const incidentTypeColors: Record<string, string> = {
        ACTIVE_SHOOTER: "danger",
        KNIFE_ATTACK: "warning",
        VIOLENT_ACTIVITY: "danger",
        FIRE_EMERGENCY: "danger",
    };

    return (
        <React.Fragment>
            <div className="table-responsive">
                <table className="table table-hover">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>{t("protocol.title")}</th>
                            <th>{t("institution.institution")}</th>
                            <th>{t("incident.incidentType")}</th>
                            <th>{t("beacon.beaconUuid")}</th>
                            <th className="text-end">{t("general.actions")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {protocols?.length > 0 ? (
                            protocols.map((protocol, index) => (
                                <tr key={protocol.id}>
                                    <td>{pagination.page * pagination.size + index + 1}</td>
                                    <td>
                                        <h6 className="mb-0">{protocol.title}</h6>
                                    </td>
                                    <td>{protocol.institutionName || "-"}</td>
                                    <td>
                                        {protocol.incidentType ? (
                                            <Badge
                                                bg={incidentTypeColors[protocol.incidentType] || "secondary"}
                                            >
                                                {t(`incidentType.${protocol.incidentType}`)}
                                            </Badge>
                                        ) : (
                                            "-"
                                        )}
                                    </td>
                                    <td>
                                        <code className="text-muted">{protocol.beaconUuid || "-"}</code>
                                    </td>
                                    <td className="text-end">
                                        <Link
                                            to={`/protocols/${protocol.id}`}
                                            className="avtar avtar-xs btn-link-secondary"
                                        >
                                            <i className="ti ti-edit f-20"></i>
                                        </Link>
                                        <Link
                                            to="#"
                                            onClick={() => {
                                                setSelectedProtocol(protocol);
                                                setIsDeleteVisible(true);
                                            }}
                                            className="avtar avtar-xs btn-link-secondary"
                                        >
                                            <i className="ti ti-trash f-20"></i>
                                        </Link>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={6} className="text-center">
                                    {t("general.noRecordsFound")}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="d-flex justify-content-between align-items-center mt-3">
                <PageSizeSelect
                    value={pagination.size}
                    onChange={(size) => changeFilterHandler("size", size)}
                />
                <Pagination
                    currentPage={pagination.page}
                    totalPages={totalPages}
                    onPageChange={(page) => changeFilterHandler("page", page)}
                />
            </div>

            <DeleteModal
                show={isDeleteVisible}
                handleClose={handleCloseDelete}
                handleDeleteId={() => handleDeleteProtocol(selectedProtocol?.id!)}
                name={selectedProtocol?.title}
            />
        </React.Fragment>
    );
};

export default ProtocolList;
